import React from 'react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import Seo, { serviceLd } from '../../components/Seo';
import { MessageSquare, Globe, Shield, Zap, CheckCircle2, BarChart } from 'lucide-react';

const SMS = () => {
  return (
    <div className="min-h-screen pt-24 pb-16">
      <Seo
        title="SMS Messaging Platform: A2P & 2-Way SMS"
        description="Global A2P and P2P SMS messaging from VOIP2 with campaign registration, compliance and real-time delivery tracking."
        path="/products/sms"
        breadcrumbs={[{ name: 'SMS', path: '/products/sms' }]}
        jsonLd={serviceLd({ name: 'SMS Messaging', description: 'Global A2P and P2P SMS messaging from VOIP2 with campaign registration, compliance and real-time delivery tracking.', path: '/products/sms' })}
      />
      {/* Hero Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold mb-6" style={{ color: '#0a1f2e' }}>
                SMS Messaging Platform
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Global A2P and P2P messaging solutions with campaign registration, compliance, and real-time delivery tracking.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  style={{ backgroundColor: '#c8ff00', color: '#0a1f2e' }}
                  className="hover:opacity-90 font-semibold"
                  onClick={() => window.location.href = '/contact'}
                >
                  Get Started
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => window.location.href = '/contact'}
                >
                  View Pricing
                </Button>
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#0a1f2e] to-[#0a2f3e] rounded-lg p-6 sm:p-12 text-white">
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-2xl sm:text-4xl font-bold" style={{ color: '#c8ff00' }}>150+</div>
                  <div className="text-sm mt-2">Countries</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-4xl font-bold" style={{ color: '#c8ff00' }}>99.9%</div>
                  <div className="text-sm mt-2">Delivery Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-4xl font-bold" style={{ color: '#c8ff00' }}>&lt;3s</div>
                  <div className="text-sm mt-2">Avg Delivery</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-4xl font-bold" style={{ color: '#c8ff00' }}>24/7</div>
                  <div className="text-sm mt-2">Support</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#0a1f2e' }}>
            SMS Platform Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="p-6 hover:shadow-xl transition-all duration-300">
              <MessageSquare className="w-12 h-12 mb-4 text-[#0a1f2e]" />
              <h3 className="text-xl font-bold mb-2" style={{ color: '#0a1f2e' }}>Two-Way Messaging</h3>
              <p className="text-gray-600">Send and receive SMS with full conversation management</p>
            </Card>
            <Card className="p-6 hover:shadow-xl transition-all duration-300">
              <Globe className="w-12 h-12 mb-4 text-[#0a1f2e]" />
              <h3 className="text-xl font-bold mb-2" style={{ color: '#0a1f2e' }}>Global Coverage</h3>
              <p className="text-gray-600">Reach customers in 150+ countries worldwide</p>
            </Card>
            <Card className="p-6 hover:shadow-xl transition-all duration-300">
              <Shield className="w-12 h-12 mb-4 text-[#0a1f2e]" />
              <h3 className="text-xl font-bold mb-2" style={{ color: '#0a1f2e' }}>Compliance Ready</h3>
              <p className="text-gray-600">Full compliance with US A2P 10DLC and international regulations</p>
            </Card>
            <Card className="p-6 hover:shadow-xl transition-all duration-300">
              <Zap className="w-12 h-12 mb-4 text-[#0a1f2e]" />
              <h3 className="text-xl font-bold mb-2" style={{ color: '#0a1f2e' }}>High Throughput</h3>
              <p className="text-gray-600">Send thousands of messages per second with guaranteed delivery</p>
            </Card>
            <Card className="p-6 hover:shadow-xl transition-all duration-300">
              <BarChart className="w-12 h-12 mb-4 text-[#0a1f2e]" />
              <h3 className="text-xl font-bold mb-2" style={{ color: '#0a1f2e' }}>Real-time Analytics</h3>
              <p className="text-gray-600">Track delivery, engagement, and campaign performance</p>
            </Card>
            <Card className="p-6 hover:shadow-xl transition-all duration-300">
              <CheckCircle2 className="w-12 h-12 mb-4 text-[#0a1f2e]" />
              <h3 className="text-xl font-bold mb-2" style={{ color: '#0a1f2e' }}>API Integration</h3>
              <p className="text-gray-600">RESTful API for seamless integration with your systems</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16 bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#0a1f2e' }}>
            Perfect For
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="p-6 text-center hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0a1f2e' }}>Marketing Campaigns</h3>
              <p className="text-gray-600">Send promotional messages and offers to your audience</p>
            </Card>
            <Card className="p-6 text-center hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0a1f2e' }}>OTP & 2FA</h3>
              <p className="text-gray-600">Secure authentication and verification codes</p>
            </Card>
            <Card className="p-6 text-center hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0a1f2e' }}>Notifications</h3>
              <p className="text-gray-600">Order updates, delivery alerts, and reminders</p>
            </Card>
            <Card className="p-6 text-center hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0a1f2e' }}>Customer Support</h3>
              <p className="text-gray-600">Two-way conversations with your customers</p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="p-12 text-center bg-gradient-to-br from-[#0a1f2e] to-[#0a2f3e] text-white">
            <h2 className="text-3xl font-bold mb-4">Start sending messages today</h2>
            <p className="text-xl text-gray-300 mb-8">Join thousands of businesses using VOIP2 SMS</p>
            <Button 
              size="lg"
              style={{ backgroundColor: '#c8ff00', color: '#0a1f2e' }}
              className="hover:opacity-90 font-semibold"
              onClick={() => window.location.href = '/contact'}
            >
              Get Started
            </Button>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default SMS;